// @flow
import _ from 'lodash'
import type { Store } from '../ducks'
import type {
  ValidatedAdvertisement,
  ValidatedAdvertisementWithStoredMedia
} from '../../components/types'
import type { CarouselCard } from '../ducks/carouselEdit/reducers'
import type { ErrorsPack } from '../../components/initErrorsFromResponse'
import {
  defaultFbLinkButton,
  defaultTmLinkButton,
  defaultVkLinkButton
} from '../../components/constants'

export const getMedia = (state: Store, storeId: string) => state.medias[storeId]

export const getAdvertisement = (
  state: Store,
  storeId: string
): ValidatedAdvertisementWithStoredMedia => state.advertisements[storeId]

export const getAdvertisements = (state: Store, storeIds: Array<string>) =>
  _.values(_.pick(state.advertisements, storeIds))

export function getCarousel(state: Store, storeId: string) {
  return state.carouselEdit[storeId]
}

export const vkLinkButtonValue = (advertisement: ValidatedAdvertisementWithStoredMedia): string =>
  advertisement.vkLinkButton ? advertisement.vkLinkButton : defaultVkLinkButton

export const tmLinkButtonValue = (advertisement: ValidatedAdvertisementWithStoredMedia): string =>
  advertisement.tmLinkButton ? advertisement.tmLinkButton : defaultTmLinkButton

export const fbLinkButtonValue = (advertisement: ValidatedAdvertisementWithStoredMedia): string =>
  advertisement.fbLinkButton ? advertisement.fbLinkButton : defaultFbLinkButton

export function getAdvertisementWithMedia(state: Store, storeId: string): ValidatedAdvertisement {
  const advertisement: ValidatedAdvertisementWithStoredMedia = getAdvertisement(state, storeId)
  return toValidatedAdvertisementWithMedia(state, advertisement)
}

export function getReplicaAdvertisementWithMedia(
  state: Store,
  storeId: string,
  replicaIndex: number
): ValidatedAdvertisement {
  const advertisement = getReplicaAdvertisement(state, storeId, replicaIndex)
  const advertisementWithSmallImageCase: ValidatedAdvertisementWithStoredMedia = {
    ...advertisement,
    tmSmallImageStoreId: advertisement.tmSmallImageStoreId || advertisement.multiImageStoreId
  }

  return toValidatedAdvertisementWithMedia(state, advertisementWithSmallImageCase)
}

export function getReplicaAdvertisement(
  state: Store,
  storeId: string,
  replicaIndex: number
): ValidatedAdvertisementWithStoredMedia {
  const ad = _.cloneDeep(getAdvertisement(state, storeId))
  const replica = state.advertisementReplicas[replicaIndex]

  return {
    ...ad,
    multiImageStoreId: replica.multiImageStoreId,
    videoStoreId: replica.videoStoreId,
    logoImageStoreId: ad.logoImageStoreId,
    logoImageCrop: ad.logoImageCrop,
    logoImageErrors: ad.logoImageErrors,
    snippetImageStoreId: replica.snippetImageStoreId,
    snippetImageCrop: replica.snippetImageCrop,
    snippetImageErrors: replica.snippetImageErrors,
    vkColumnCrop: replica.vkColumnImageCrop,
    vkMultiCrop: replica.vkMultiImageCrop,
    vkArticleCrop: replica.vkArticleImageCrop,
    vkSiteCrop: replica.vkSiteImageCrop,
    vkBigImageStoreId: replica.vkBigImageStoreId,
    vkBigImageErrors: replica.vkBigImageErrors,
    tmColumnCrop: replica.tmColumnImageCrop,
    tmMultiCrop: replica.tmMultiImageCrop,
    tmSmallImageStoreId: replica.tmSmallImageStoreId || replica.multiImageStoreId,
    tmSmallImageCrop: replica.tmSmallImageCrop,
    tmSmallImageErrors: replica.tmSmallImageErrors,
    fbImageCrop: replica.fbImageCrop,
    fbInstagramImageStoreId: replica.fbInstagramImageStoreId,
    fbInstagramImageCrop: replica.fbInstagramImageCrop,
    fbInstagramImageErrors: replica.fbInstagramImageErrors,
    fbStoriesImageStoreId: replica.fbStoriesImageStoreId,
    fbStoriesImageCrop: replica.fbStoriesImageCrop,
    fbStoriesImageErrors: replica.fbStoriesImageErrors,
    fbThumbnailImageStoreId: replica.fbThumbnailImageStoreId,
    fbThumbnailImageCrop: replica.fbThumbnailImageCrop,
    fbThumbnailImageErrors: replica.fbThumbnailImageErrors,
    glImageCrop: replica.glImageCrop
  }
}

function toValidatedAdvertisementWithMedia(
  state: Store,
  advertisement: ValidatedAdvertisementWithStoredMedia
): ValidatedAdvertisement {
  const carouselStoreId = advertisement.carouselStoreId

  return {
    advertisementTemplateId: advertisement.advertisementTemplateId,
    carousel: carouselStoreId ? getCarousel(state, carouselStoreId) : null,
    columnDescription: advertisement.columnDescription,
    columnDescriptionErrors: advertisement.columnDescriptionErrors,
    columnImage: saveGetMedia(state, advertisement.columnImageStoreId),
    columnImageErrors: advertisement.columnImageErrors,
    fbImageCrop: advertisement.fbImageCrop,
    fbInstagramImage: saveGetMedia(state, advertisement.fbInstagramImageStoreId),
    fbInstagramImageCrop: advertisement.fbInstagramImageCrop,
    fbInstagramImageErrors: advertisement.fbInstagramImageErrors,
    fbLinkButton: fbLinkButtonValue(advertisement),
    fbLinkButtonErrors: advertisement.fbLinkButtonErrors,
    fbMultiDescription: advertisement.fbMultiDescription,
    fbMultiDescriptionErrors: advertisement.fbMultiDescriptionErrors,
    fbStoriesImage: saveGetMedia(state, advertisement.fbStoriesImageStoreId),
    fbStoriesImageCrop: advertisement.fbStoriesImageCrop,
    fbStoriesImageErrors: advertisement.fbStoriesImageErrors,
    fbThumbnailImage: saveGetMedia(state, advertisement.fbThumbnailImageStoreId),
    fbThumbnailImageCrop: advertisement.fbThumbnailImageCrop,
    fbThumbnailImageErrors: advertisement.fbThumbnailImageErrors,
    fbTitle: advertisement.fbTitle,
    fbTitleErrors: advertisement.fbTitleErrors,
    fbUrl: advertisement.fbUrl,
    fbUrlErrors: advertisement.fbUrlErrors,
    glImageCrop: advertisement.glImageCrop,
    id: advertisement.id,
    logoImage: saveGetMedia(state, advertisement.logoImageStoreId),
    logoImageCrop: advertisement.logoImageCrop,
    logoImageErrors: advertisement.logoImageErrors,
    multiDescription: advertisement.multiDescription,
    multiDescriptionErrors: advertisement.multiDescriptionErrors,
    multiImage: saveGetMedia(state, advertisement.multiImageStoreId),
    multiImageErrors: advertisement.multiImageErrors,
    productDescription: advertisement.productDescription,
    productDescriptionErrors: advertisement.productDescriptionErrors,
    productTitle: advertisement.productTitle,
    productTitleErrors: advertisement.productTitleErrors,
    snippetImage: saveGetMedia(state, advertisement.snippetImageStoreId),
    snippetImageCrop: advertisement.snippetImageCrop,
    snippetImageErrors: advertisement.snippetImageErrors,
    title: advertisement.title,
    titleErrors: advertisement.titleErrors,
    tmCarouselDescription: advertisement.tmCarouselDescription,
    tmCarouselDescriptionErrors: advertisement.tmCarouselDescriptionErrors,
    tmColumnCrop: advertisement.tmColumnCrop,
    tmLinkButton: tmLinkButtonValue(advertisement),
    tmLinkButtonErrors: advertisement.tmLinkButtonErrors,
    tmMultiCrop: advertisement.tmMultiCrop,
    tmSmallImage: saveGetMedia(state, advertisement.tmSmallImageStoreId),
    tmSmallImageCrop: advertisement.tmSmallImageCrop,
    tmSmallImageErrors: advertisement.tmSmallImageErrors,
    url: advertisement.url,
    urlErrors: advertisement.urlErrors,
    video: saveGetMedia(state, advertisement.videoStoreId),
    videoErrors: advertisement.videoErrors,
    vkArticleCrop: advertisement.vkArticleCrop,
    vkBigImage: saveGetMedia(state, advertisement.vkBigImageStoreId),
    vkBigImageErrors: advertisement.vkBigImageErrors,
    vkColumnCrop: advertisement.vkColumnCrop,
    vkLinkButton: vkLinkButtonValue(advertisement),
    vkLinkButtonErrors: advertisement.vkLinkButtonErrors,
    vkLinkTitle: advertisement.vkLinkTitle,
    vkLinkTitleErrors: advertisement.vkLinkTitleErrors,
    vkMultiCrop: advertisement.vkMultiCrop,
    vkMultiDescription: advertisement.vkMultiDescription,
    vkMultiDescriptionErrors: advertisement.vkMultiDescriptionErrors,
    vkSiteCrop: advertisement.vkSiteCrop,
    vkTitle: advertisement.vkTitle,
    vkTitleErrors: advertisement.vkTitleErrors,
    vkUrl: advertisement.vkUrl,
    vkUrlErrors: advertisement.vkUrlErrors,
    vkWithoutButtonDescription: advertisement.vkWithoutButtonDescription,
    vkWithoutButtonDescriptionErrors: advertisement.vkWithoutButtonDescriptionErrors
  }
}

function saveGetMedia(state: Store, storeId: ?string) {
  return storeId ? getMedia(state, storeId) : null
}

export function hasAdvertisementAnyErrors(state: Store, storeId: string) {
  const advertisement: ValidatedAdvertisementWithStoredMedia = getAdvertisement(state, storeId)
  const errors: ErrorsPack = {
    commonErrors: {
      columnDescriptionErrors: advertisement.columnDescriptionErrors,
      fbLinkButtonErrors: advertisement.fbLinkButtonErrors,
      fbMultiDescriptionErrors: advertisement.fbMultiDescriptionErrors,
      fbTitleErrors: advertisement.fbTitleErrors,
      fbUrlErrors: advertisement.fbUrlErrors,
      logoImageErrors: advertisement.logoImageErrors,
      multiDescriptionErrors: advertisement.multiDescriptionErrors,
      productDescriptionErrors: advertisement.productDescriptionErrors,
      productTitleErrors: advertisement.productTitleErrors,
      snippetImageErrors: advertisement.snippetImageErrors,
      titleErrors: advertisement.titleErrors,
      tmCarouselDescriptionErrors: advertisement.tmCarouselDescriptionErrors,
      tmLinkButtonErrors: advertisement.tmLinkButtonErrors,
      tmUrlErrors: advertisement.tmUrlErrors,
      urlErrors: advertisement.urlErrors,
      vkLinkButtonErrors: advertisement.vkLinkButtonErrors,
      vkLinkTitleErrors: advertisement.vkLinkTitleErrors,
      vkMultiDescriptionErrors: advertisement.vkMultiDescriptionErrors,
      vkTitleErrors: advertisement.vkTitleErrors,
      vkUrlErrors: advertisement.vkUrlErrors,
      vkWithoutButtonDescriptionErrors: advertisement.vkWithoutButtonDescriptionErrors
    },
    mediaErrors: {
      columnImageErrors: advertisement.columnImageErrors,
      fbInstagramImageErrors: advertisement.fbInstagramImageErrors,
      fbStoriesImageErrors: advertisement.fbStoriesImageErrors,
      fbThumbnailImageErrors: advertisement.fbThumbnailImageErrors,
      multiImageErrors: advertisement.multiImageErrors,
      tmSmallImageErrors: advertisement.tmSmallImageErrors,
      videoErrors: advertisement.videoErrors,
      vkBigImageErrors: advertisement.vkBigImageErrors
    },
    carouselErrors: []
  }
  return (
    !_.every(_.values({ ...errors.commonErrors, ...errors.mediaErrors }), _.isEmpty) ||
    hasCarouselAnyErrors(state, advertisement.carouselStoreId)
  )
}

export function hasCarouselAnyErrors(state: Store, storeId: ?string) {
  if (!storeId) return false
  const carousel = getCarousel(state, storeId)

  return (
    !_.isEmpty(carousel.errors) ||
    _.some(carousel.cards, (card: CarouselCard) => hasCarouselCardAnyErrors(card))
  )
}

export function hasCarouselCardAnyErrors(card: CarouselCard) {
  const cardErrors = {
    urlErrors: card.urlErrors,
    titleErrors: card.titleErrors,
    descriptionErrors: card.descriptionErrors,
    imageErrors: card.imageErrors,
    vkLinkButtonErrors: card.vkLinkButtonErrors
  }
  return !_.every(_.values(cardErrors), _.isEmpty)
}
